import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Form, Input, FormInstance, theme, Row, Col, Space, Skeleton, Divider } from 'antd';
import { MessageInstance } from 'antd/es/message/interface';
import { useState } from 'react';

import { useResponsive } from '@/hooks/useResponsive';
import { DeviceType, compressorTypes } from '@/constants/deviceTypeEnum';
import { EPSelect, EPSelectOption } from '@/components';
import { useMeasurement } from '@/hooks/useMeasurement';
import AirTankWidget from '@/comp/common/icons/AirTankWidget';
import { useStores } from '@/hooks/use-stores';

import { Entity } from '../../../NodeWidget.types';
import {
  DEFAULT_MODEL_IDS,
  getDeprecatedModelToolTip,
  getFieldSelectProps,
  IsDeprecatedModel,
  useFormHelper,
} from '../../EditAssetDrawer.utils';
import { getTankTypeOptions, mapFormValuesToPayload, useInitialValues } from './EditTankForm.utils';
import { EditTankFormContainer } from './EditTankForm.styles';
import ConnectionSelector from '../ConnectionSelector/ConnectionSelector';
import ConnectionList from '../ConnectionList/ConnectionList';

const EditTankForm = observer(
  ({
    entity,
    form,
    afterOpenChange,
    messageApi,
  }: {
    entity: Entity;
    form: FormInstance;
    afterOpenChange: boolean;
    messageApi: MessageInstance;
  }) => {
    const { t } = useTranslation();
    const { isMobile } = useResponsive();
    const { pidEditStore } = useStores();
    const { tunit } = useMeasurement();
    const { token } = theme.useToken();
    const [currentDeviceType, setCurrentDeviceType] = useState(entity.deviceType);
    const { deviceModelOptions, isLoadingModels, onFinish, onModelSearch, initialValues, deviceConnectionOptions } =
      useFormHelper(
        t,
        entity,
        afterOpenChange,
        messageApi,
        mapFormValuesToPayload,
        useInitialValues,
        currentDeviceType,
      );

    const isLoading = !afterOpenChange || pidEditStore.isLoadingDraft;
    const deprecatedModel = IsDeprecatedModel(entity.deviceKind);

    const onDeviceTypeChange = newDeviceType => {
      setCurrentDeviceType(newDeviceType);
      form.setFieldValue('deviceModelId', DEFAULT_MODEL_IDS[newDeviceType]);
      form.setFieldValue('connectFrom', []);
      form.setFieldValue('connectTo', []);
    };

    if (isLoading) {
      return <Skeleton active paragraph={{ rows: 6 }} />;
    }

    return (
      <EditTankFormContainer>
        <Form layout="vertical" form={form} initialValues={initialValues} onFinish={onFinish}>
          <span className="horizontal-label">{t('connect')}</span>
          <Row className="connected-to-row">
            <Col span={6}>
              <Form.Item label=" " name="connectFrom">
                <ConnectionSelector
                  label="from"
                  options={deviceConnectionOptions.filter(option => option.deviceType !== DeviceType.ProductionFloor)}
                  form={form}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <div className="pipe" />
            </Col>
            <Col span={4} className="widget">
              <AirTankWidget
                width={65}
                height={110}
                color={token.colorBorder}
                iconColor={token.colorBgBase}
                dry={entity.deviceType === DeviceType.DryAirTank}
              />
            </Col>
            <Col span={4}>
              <div className="pipe" />
            </Col>
            <Col span={6}>
              <Form.Item label=" " name="connectTo">
                <ConnectionSelector
                  label="to"
                  options={deviceConnectionOptions.filter(option => !compressorTypes.includes(option.deviceType))}
                  form={form}
                />
              </Form.Item>
            </Col>
          </Row>
          <ConnectionList form={form} field="from" />
          <ConnectionList form={form} field="to" />
          <Divider className="divider" />
          <Form.Item label={t('name')} name="name">
            <Input size="large" />
          </Form.Item>
          <Form.Item label={t('type')} name="deviceType">
            <EPSelect
              {...getFieldSelectProps(isMobile, form, t('selectType'), 'deviceType')}
              options={getTankTypeOptions(t)}
              disabled={!!entity.deviceId}
              onChange={onDeviceTypeChange}
            />
          </Form.Item>
          <Form.Item
            label={t('modelSpec')}
            name="deviceModelId"
            tooltip={deprecatedModel ? getDeprecatedModelToolTip() : undefined}
            required
            rules={[{ required: true, message: t('required') }]}
          >
            <EPSelect
              {...getFieldSelectProps(isMobile, form, t('selectModelSpec'), 'deviceModelId')}
              loading={isLoadingModels}
              onSearch={onModelSearch}
              filterOption={false}
              disabled={deprecatedModel}
            >
              {deviceModelOptions.map(option => (
                <EPSelectOption key={option.value} value={option.value}>
                  {option.label}
                </EPSelectOption>
              ))}
            </EPSelect>
          </Form.Item>
          <Space.Compact style={{ width: '100%' }}>
            <Form.Item label={t('Capacity')} name="capacity" style={{ width: '80%' }}>
              <Input size="large" />
            </Form.Item>
            <Form.Item label=" " style={{ width: '20%' }}>
              <Input value={tunit('Liter')} disabled size="large" />
            </Form.Item>
          </Space.Compact>
        </Form>
      </EditTankFormContainer>
    );
  },
);

export default EditTankForm;
